import { useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import getEndpoints from "lib/apiEndpoints";

function buildAuthPayload() {
  const session = JSON.parse(localStorage.getItem("ApiCredentials") || "null");
  const kIdUser =
    localStorage.getItem("k-user-vision") || localStorage.getItem("k-user");

  const payload = {
    params: {
      header: {},
    },
  };

  if (kIdUser) {
    payload.params.header["K-IdUser"] = kIdUser;
  }

  if (session?.accessToken) {
    payload.Authorization = `Bearer ${session.accessToken}`;
  }

  return payload;
}

function getBaseWsUrl() {
  return getEndpoints().logWs.split("?")[0];
}

export default function useWebsocketLogDashboard({
  onDashboardSuccess,
  onTimelineSuccess,
  onError,
}) {
  const [socketUrl, setSocketUrl] = useState(null);
  const [status, setStatus] = useState("off");
  const pendingActionRef = useRef(null);
  const pendingPayloadRef = useRef(null);
  const lastResponseRef = useRef(null);
  const statusRef = useRef(status);
  const readyStateRef = useRef(ReadyState.UNINSTANTIATED);
  const sendJsonMessageRef = useRef(() => {});

  const onDashboardSuccessRef = useRef(onDashboardSuccess);
  const onTimelineSuccessRef = useRef(onTimelineSuccess);
  const onErrorRef = useRef(onError);

  onDashboardSuccessRef.current = onDashboardSuccess;
  onTimelineSuccessRef.current = onTimelineSuccess;
  onErrorRef.current = onError;
  statusRef.current = status;

  const finishWithError = (message) => {
    pendingActionRef.current = null;
    pendingPayloadRef.current = null;
    lastResponseRef.current = null;
    statusRef.current = "off";
    setStatus("off");
    onErrorRef.current?.(message);
  };

  const handleResponse = (response) => {
    if (!response || pendingActionRef.current == null) {
      return;
    }

    const isSuccess = response?.Success ?? response?.success;
    const result = response?.Result ?? response?.result;

    statusRef.current = "off";
    setStatus("off");

    if (response?.message === "Internal server error") {
      finishWithError(
        "Erro interno no WebSocket. Verifique a rota access-dashboard no LogWS.",
      );
      return;
    }

    if (isSuccess === false) {
      finishWithError(
        response?.ValidationMessage ||
          response?.validationMessage ||
          "Não foi possível carregar os indicadores de acesso.",
      );
      return;
    }

    if (pendingActionRef.current === "access-dashboard") {
      onDashboardSuccessRef.current?.(result);
    }

    if (pendingActionRef.current === "access-log-timeline") {
      onTimelineSuccessRef.current?.(result);
    }

    pendingActionRef.current = null;
    pendingPayloadRef.current = null;
    lastResponseRef.current = null;
  };

  const flushPendingPayload = () => {
    if (!pendingPayloadRef.current) {
      return;
    }

    const payload = pendingPayloadRef.current;
    pendingPayloadRef.current = null;
    sendJsonMessageRef.current(payload);
  };

  const openConnection = () => {
    const baseUrl = getBaseWsUrl();
    setSocketUrl(`${baseUrl}?t=${Date.now()}`);
  };

  const { sendJsonMessage, readyState } = useWebSocket(socketUrl, {
    onOpen: () => {
      if (statusRef.current !== "fetching") {
        statusRef.current = "on";
        setStatus("on");
      }

      flushPendingPayload();
    },
    onClose: () => {
      if (lastResponseRef.current && pendingActionRef.current) {
        handleResponse(lastResponseRef.current);
        return;
      }

      if (pendingPayloadRef.current) {
        return;
      }

      if (pendingActionRef.current && statusRef.current === "fetching") {
        finishWithError(
          "A conexão foi encerrada antes de receber os dados do dashboard.",
        );
      }
    },
    onError: () => {
      if (pendingPayloadRef.current) {
        return;
      }

      finishWithError("Erro na conexão WebSocket de indicadores de acesso.");
    },
    onMessage: (event) => {
      try {
        const response = JSON.parse(event.data);
        lastResponseRef.current = response;
        handleResponse(response);
      } catch {
        // frame não-JSON (ignorar)
      }
    },
    shouldReconnect: () => false,
  });

  sendJsonMessageRef.current = sendJsonMessage;
  readyStateRef.current = readyState;

  const connect = () => {
    openConnection();
  };

  const sendAction = (action, querystring) => {
    pendingActionRef.current = action;
    lastResponseRef.current = null;
    statusRef.current = "fetching";
    setStatus("fetching");

    const basePayload = buildAuthPayload();
    const payload = {
      ...basePayload,
      action,
      params: {
        ...basePayload.params,
        querystring,
      },
    };

    if (readyStateRef.current === ReadyState.OPEN) {
      sendJsonMessage(payload);
      return;
    }

    pendingPayloadRef.current = payload;
    openConnection();
  };

  const fetchDashboard = (startDate, endDate, idsPerfil) => {
    const querystring = {
      dataInicial: startDate,
      dataFinal: endDate,
    };

    if (idsPerfil?.length) {
      querystring.idsPerfil = idsPerfil.join(",");
    }

    sendAction("access-dashboard", querystring);
  };

  const fetchTimeline = ({
    startDate,
    endDate,
    page = 0,
    pageSize = 10,
    idUsuario,
    tela,
    sistema,
  }) => {
    const querystring = {
      dataInicial: startDate,
      dataFinal: endDate,
      page,
      pageSize,
    };

    if (idUsuario != null) {
      querystring.idUsuario = idUsuario;
    }

    if (tela) {
      querystring.tela = tela;
    }

    if (sistema) {
      querystring.sistema = sistema;
    }

    sendAction("access-log-timeline", querystring);
  };

  return {
    connect,
    fetchDashboard,
    fetchTimeline,
    readyState,
    status,
  };
}
